(() => {
  const SELECTOR = '[data-resonance]';
  const escapeHtml = (value = '') => String(value).replace(/[&<>"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]);
  const uid = () => `fragrance-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  class ResonanceEvaluation {
    constructor(root) {
      this.root = root;
      this.steps = [...root.querySelectorAll('[data-step]')];
      this.config = JSON.parse(root.querySelector('[data-config]').textContent);
      this.key = `resonance:${root.dataset.sectionId}`;
      const restoredState = this.restore();
      this.state = restoredState || this.initialState();
      this.index = Math.min(this.state.step || 0, Math.max(this.steps.length - 1, 0));
      if (restoredState && this.steps[this.index]?.dataset.stepType === 'outcome') this.state.outcome = '';
      this.bind();
      this.go(this.index);
    }

    initialState() {
      return {
        step: 0,
        evalIndex: 0,
        outcome: '',
        winnerId: '',
        fragrances: Array.from({ length: this.config.defaultCount }, () => this.newFragrance()),
        ranking: []
      };
    }

    newFragrance(name = '') {
      return { id: uid(), name, first: '', settle: '', sliders: [50, 50, 50, 50], affinity: 50, notes: '' };
    }

    restore() {
      if (this.root.dataset.saveProgress !== 'true') return null;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.key));
        return saved && Array.isArray(saved.fragrances) ? saved : null;
      } catch (_) { return null; }
    }

    save() {
      this.state.step = this.index;
      if (this.root.dataset.saveProgress === 'true') {
        try { sessionStorage.setItem(this.key, JSON.stringify(this.state)); } catch (_) {}
      }
    }

    bind() {
      this.root.addEventListener('click', event => {
        const count = event.target.closest('[data-count-change]');
        if (count) return this.changeCount(Number(count.dataset.countChange));
        const next = event.target.closest('[data-next]');
        if (next) return this.next();
        const back = event.target.closest('[data-back]');
        if (back) return this.back();
        const chip = event.target.closest('[data-chip]');
        if (chip) return this.selectChip(chip);
        const rank = event.target.closest('[data-rank-move]');
        if (rank) return this.moveRank(rank.dataset.id, Number(rank.dataset.rankMove));
        const choice = event.target.closest('[data-winner]');
        if (choice) return this.chooseWinner(choice.dataset.winner);
        const outcome = event.target.closest('[data-outcome]');
        if (outcome) return this.chooseOutcome(outcome.dataset.outcome);
      });

      this.root.addEventListener('input', event => {
        if (event.target.matches('[data-fragrance-name]')) {
          const fragrance = this.byId(event.target.dataset.id);
          if (fragrance) fragrance.name = event.target.value;
          this.state.winnerId = '';
          this.state.outcome = '';
        }
        if (event.target.matches('[data-slider]')) {
          const fragrance = this.currentFragrance();
          fragrance.sliders[Number(event.target.dataset.slider)] = Number(event.target.value);
          this.state.winnerId = '';
          this.state.outcome = '';
        }
        if (event.target.matches('[data-affinity]')) {
          const fragrance = this.currentFragrance();
          fragrance.affinity = Number(event.target.value);
          this.state.ranking = [];
          this.state.winnerId = '';
          this.state.outcome = '';
          const output = this.root.querySelector('[data-affinity-output]');
          if (output) output.textContent = this.affinityWord(fragrance.affinity);
        }
        if (event.target.matches('[data-evaluation-notes]')) {
          this.currentFragrance().notes = event.target.value;
          this.state.winnerId = '';
          this.state.outcome = '';
        }
        this.save();
      });

      this.root.addEventListener('pointerdown', event => {
        const handle = event.target.closest('[data-drag-handle]');
        if (handle) this.beginRankDrag(event, handle);
      });
      this.root.addEventListener('keydown', event => {
        const handle = event.target.closest('[data-drag-handle]');
        if (!handle || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        this.moveRank(handle.dataset.id, event.key === 'ArrowUp' ? -1 : 1);
      });

      const form = this.root.querySelector('[data-form]');
      if (form) form.addEventListener('submit', event => this.submit(event));
    }

    currentStep() { return this.steps[this.index]; }
    currentFragrance() { return this.state.fragrances[this.state.evalIndex]; }
    byId(id) { return this.state.fragrances.find(item => item.id === id); }
    typeIndex(type) { return this.steps.findIndex(step => step.dataset.stepType === type); }

    go(index) {
      if (index < 0 || index >= this.steps.length) return;
      const previousType = this.currentStep()?.dataset.stepType;
      const nextType = this.steps[index]?.dataset.stepType;
      if (previousType === 'decision' && nextType === 'outcome') this.state.outcome = '';
      this.steps.forEach((step, i) => {
        const active = i === index;
        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', String(!active));
      });
      this.index = index;
      this.save();
      this.renderStep();
      this.currentStep().scrollTop = 0;
      const heading = this.currentStep().querySelector('[data-step-heading], h2');
      if (heading) requestAnimationFrame(() => heading.focus({ preventScroll: true }));
      const announcer = this.root.querySelector('[data-announcer]');
      if (announcer) announcer.textContent = `Step ${index + 1} of ${this.steps.length}`;
    }

    next() {
      const type = this.currentStep().dataset.stepType;
      if (type === 'fragrances' && !this.validateNames()) return;
      if (type === 'evaluation') {
        if (this.state.evalIndex < this.state.fragrances.length - 1) {
          this.state.evalIndex += 1;
          this.save();
          this.renderEvaluation();
          return;
        }
        this.prepareRanking();
      }
      if (type === 'results' && this.state.fragrances.length === 1) {
        this.state.winnerId = this.state.fragrances[0].id;
        const outcome = this.typeIndex('outcome');
        return this.go(outcome >= 0 ? outcome : this.index + 1);
      }
      if (type === 'decision' && !this.state.winnerId) return;
      if (type === 'outcome' && !this.state.outcome) return;
      this.go(this.index + 1);
    }

    back() {
      if (this.currentStep().dataset.stepType === 'evaluation' && this.state.evalIndex > 0) {
        this.state.evalIndex -= 1;
        this.save();
        return this.renderEvaluation();
      }
      this.go(this.index - 1);
    }

    renderStep() {
      const progress = this.root.querySelector('[data-progress]');
      if (progress) progress.style.width = `${this.steps.length > 1 ? (this.index / (this.steps.length - 1)) * 100 : 100}%`;
      const type = this.currentStep()?.dataset.stepType;
      if (type === 'fragrances') this.renderNames();
      if (type === 'evaluation') this.renderEvaluation();
      if (type === 'results') this.renderResults();
      if (type === 'decision') this.renderDecision();
      if (type === 'outcome') this.renderOutcome();
      if (type === 'contact') this.renderContact();
      if (type === 'success') this.renderSuccess();
    }

    changeCount(delta) {
      const min = Math.min(this.config.minCount, this.config.maxCount);
      const max = Math.max(this.config.minCount, this.config.maxCount);
      const target = Math.max(min, Math.min(max, this.state.fragrances.length + delta));
      while (this.state.fragrances.length < target) this.state.fragrances.push(this.newFragrance());
      if (this.state.fragrances.length > target) this.state.fragrances.length = target;
      this.state.ranking = [];
      this.state.winnerId = '';
      this.state.outcome = '';
      this.save();
      this.renderNames();
    }

    renderNames() {
      const host = this.currentStep().querySelector('[data-fragrance-fields]');
      const output = this.currentStep().querySelector('[data-count]');
      if (!host) return;
      if (output) output.textContent = this.state.fragrances.length;
      host.innerHTML = this.state.fragrances.map((item, index) => `
        <div class="resonance__field">
          <label for="Fragrance-${this.root.dataset.sectionId}-${item.id}">${escapeHtml(this.config.labels.fragrance)} ${index + 1}</label>
          <input id="Fragrance-${this.root.dataset.sectionId}-${item.id}" data-fragrance-name placeholder="Name as shown on the bottle" data-id="${item.id}" value="${escapeHtml(item.name)}" required>
        </div>`).join('');
    }

    validateNames() {
      const inputs = [...this.currentStep().querySelectorAll('[data-fragrance-name]')];
      let valid = true;
      inputs.forEach(input => {
        const empty = !input.value.trim();
        input.setAttribute('aria-invalid', String(empty));
        if (empty && valid) input.focus();
        valid = valid && !empty;
      });
      let message = this.currentStep().querySelector('[data-name-error]');
      if (!message) {
        message = document.createElement('p');
        message.dataset.nameError = '';
        message.className = 'resonance__message';
        message.setAttribute('role', 'alert');
        this.currentStep().querySelector('[data-fragrance-fields]').after(message);
      }
      message.textContent = valid ? '' : this.config.labels.requiredNames;
      if (valid) {
        this.state.fragrances.forEach(item => { item.name = item.name.trim(); });
        this.save();
      }
      return valid;
    }

    chips(options, selected, group) {
      return options.filter(Boolean).map(option => `<button class="resonance__chip" type="button" data-chip="${group}" data-value="${escapeHtml(option)}" aria-pressed="${selected === option}">${escapeHtml(option)}</button>`).join('');
    }

    renderEvaluation() {
      const host = this.currentStep().querySelector('[data-evaluation]');
      const item = this.currentFragrance();
      if (!host || !item) return;
      const dots = this.state.fragrances.map((_, index) => `<span class="${index === this.state.evalIndex ? 'is-current' : ''}"></span>`).join('');
      const sliders = this.config.sliderLabels.map((labels, index) => `<div class="resonance__slider"><div class="resonance__slider-labels"><span>${escapeHtml(labels[0])}</span><span>${escapeHtml(labels[1])}</span></div><input type="range" min="0" max="100" value="${item.sliders[index]}" data-slider="${index}" aria-label="${escapeHtml(labels[0])} to ${escapeHtml(labels[1])}"></div>`).join('');
      const hint = value => value ? `<p class="resonance__hint">${escapeHtml(value)}</p>` : '';
      host.innerHTML = `<div class="resonance__dots" aria-hidden="true">${dots}</div><p class="resonance__eyebrow">${escapeHtml(this.config.labels.fragrance)} ${this.state.evalIndex + 1} of ${this.state.fragrances.length}</p><h2 class="resonance__heading" tabindex="-1" data-step-heading>${escapeHtml(item.name)}</h2>${this.config.labels.evaluationIntro ? `<p class="resonance__lede">${escapeHtml(this.config.labels.evaluationIntro)}</p>` : ''}
        <section class="resonance__criterion"><h3>${escapeHtml(this.config.labels.first)}</h3>${hint(this.config.labels.firstHint)}<div class="resonance__chips">${this.chips(this.config.firstOptions, item.first, 'first')}</div></section>
        <section class="resonance__criterion"><h3>${escapeHtml(this.config.labels.settle)}</h3>${hint(this.config.labels.settleHint)}<div class="resonance__chips">${this.chips(this.config.settleOptions, item.settle, 'settle')}</div></section>
        <section class="resonance__criterion"><h3>${escapeHtml(this.config.labels.character)}</h3>${hint(this.config.labels.characterHint)}${sliders}</section>
        <section class="resonance__criterion resonance__criterion--affinity"><h3>${escapeHtml(this.config.labels.affinity)}</h3>${hint(this.config.labels.affinityHint)}<input type="range" min="0" max="100" value="${item.affinity}" data-affinity aria-label="${escapeHtml(this.config.labels.affinity)}"><div class="resonance__affinity-scale"><span>${escapeHtml(this.config.affinityWords?.[0] || 'Not for me')}</span><span>${escapeHtml(this.config.affinityWords?.[2] || 'Warming to it')}</span><span>${escapeHtml(this.config.affinityWords?.[4] || 'Captivated')}</span></div><div class="resonance__affinity" data-affinity-output>${this.affinityWord(item.affinity)}</div></section>
        <section class="resonance__criterion"><div class="resonance__field"><label>${escapeHtml(this.config.labels.notes)}</label><textarea rows="4" data-evaluation-notes placeholder="${escapeHtml(this.config.labels.notesPlaceholder)}">${escapeHtml(item.notes)}</textarea></div></section>
        <div class="resonance__actions"><button class="resonance__back" type="button" data-back>${escapeHtml(this.config.labels.previous)}</button><button class="resonance__button" type="button" data-next>${escapeHtml(this.state.evalIndex === this.state.fragrances.length - 1 ? this.config.labels.results : this.config.labels.next)}</button></div>`;
    }

    selectChip(button) {
      const item = this.currentFragrance();
      item[button.dataset.chip] = button.dataset.value;
      this.state.winnerId = '';
      this.state.outcome = '';
      this.save();
      button.parentElement.querySelectorAll('[data-chip]').forEach(chip => chip.setAttribute('aria-pressed', String(chip === button)));
    }

    affinityWord(value) {
      const words = this.config.affinityWords || ['Not for me', 'Curious', 'Warming to it', 'Enticed', 'Captivated'];
      if (value < 20) return words[0];
      if (value < 40) return words[1];
      if (value < 60) return words[2];
      if (value < 80) return words[3];
      return words[4];
    }

    prepareRanking() {
      const ids = this.state.fragrances.map(item => item.id);
      if (this.state.ranking.length !== ids.length || ids.some(id => !this.state.ranking.includes(id))) {
        this.state.ranking = [...this.state.fragrances].sort((a, b) => b.affinity - a.affinity).map(item => item.id);
      }
      this.save();
    }

    renderResults() {
      this.prepareRanking();
      const cards = this.currentStep().querySelector('[data-result-cards]');
      const ranking = this.currentStep().querySelector('[data-ranking]');
      if (cards) cards.innerHTML = this.state.ranking.map((id, index) => {
        const item = this.byId(id);
        const character = this.config.sliderLabels.map((labels, sliderIndex) => {
          const value = Math.max(0, Math.min(100, Number(item.sliders?.[sliderIndex] ?? 50)));
          return `<div class="resonance__character"><div class="resonance__character-labels"><span>${escapeHtml(labels[0])}</span><span>${escapeHtml(labels[1])}</span></div><div class="resonance__character-track" role="img" aria-label="${escapeHtml(labels[0])} to ${escapeHtml(labels[1])}: ${value} percent"><span style="left:${value}%"></span></div></div>`;
        }).join('');
        const affinity = Math.max(0, Math.min(100, Number(item.affinity ?? 50)));
        const firstTag = item.first ? `<span class="resonance__tag">Opens <strong>${escapeHtml(item.first.toLowerCase())}</strong></span>` : '';
        const settleTag = item.settle ? `<span class="resonance__tag">Settles <strong>${escapeHtml(item.settle.toLowerCase())}</strong></span>` : '';
        const notes = item.notes ? `<div class="resonance__result-note"><span>In your words</span><p>${escapeHtml(item.notes)}</p></div>` : '';
        return `<article class="resonance__result ${index === 0 ? 'is-leading' : ''}"><div class="resonance__result-head"><strong>${escapeHtml(item.name)}</strong>${index === 0 ? '<span style="color: #a9743a;">Your top selection</span>' : ''}</div><div class="resonance__result-tags">${firstTag}${settleTag}</div><div class="resonance__characters">${character}</div><div class="resonance__draw"><div class="resonance__draw-labels"><span>Draw</span><span>${escapeHtml(this.affinityWord(affinity))}</span></div><div class="resonance__draw-track" role="img" aria-label="Draw: ${affinity} percent"><span style="width:${affinity}%"></span></div></div>${notes}</article>`;
      }).join('');
      if (ranking) ranking.innerHTML = this.state.ranking.map((id, index) => {
        const item = this.byId(id);
        const dragEnabled = this.config.rankingMode !== 'arrows';
        const grip = dragEnabled ? `<button class="resonance__rank-grip" type="button" data-drag-handle data-id="${id}" aria-label="Drag ${escapeHtml(item.name)} to a new position. Use up and down arrow keys for keyboard reordering." title="Drag to reorder">&#8942;&#8942;</button>` : '';
        const arrows = dragEnabled ? '' : `<button class="resonance__rank-button" type="button" data-rank-move="-1" data-id="${id}" ${index === 0 ? 'disabled' : ''} aria-label="Move ${escapeHtml(item.name)} up">&uarr;</button><button class="resonance__rank-button" type="button" data-rank-move="1" data-id="${id}" ${index === this.state.ranking.length - 1 ? 'disabled' : ''} aria-label="Move ${escapeHtml(item.name)} down">&darr;</button>`;
        const lead = index === 0 ? '<span class="resonance__rank-leading">Leading</span>' : '';
        return `<li class="resonance__rank ${index === 0 ? 'is-leading' : ''}" data-rank-id="${id}"><div class="nr-inner-cell"><span class="resonance__rank-position">${index + 1}</span><span class="resonance__rank-name"><strong>${escapeHtml(item.name)}</strong><small>${escapeHtml(item.first || '—')} · ${escapeHtml(this.affinityWord(item.affinity))}</small></span></div><div class="nr-pill-inner-wr">${lead}<span class="resonance__rank-controls">${grip}${arrows}</span></div></li>`;
      }).join('');
    }

    beginRankDrag(event, handle) {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      const id = handle.dataset.id;
      const row = handle.closest('[data-rank-id]');
      if (!row) return;
      row.classList.add('is-dragging');
      handle.setAttribute('aria-grabbed', 'true');
      const finish = pointerEvent => {
        document.removeEventListener('pointerup', finish);
        document.removeEventListener('pointercancel', cancel);
        row.classList.remove('is-dragging');
        handle.removeAttribute('aria-grabbed');
        const target = document.elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)?.closest('[data-rank-id]');
        if (!target || target.dataset.rankId === id || !this.root.contains(target)) return;
        const rect = target.getBoundingClientRect();
        this.moveRankTo(id, target.dataset.rankId, pointerEvent.clientY > rect.top + rect.height / 2);
      };
      const cancel = () => {
        document.removeEventListener('pointerup', finish);
        document.removeEventListener('pointercancel', cancel);
        row.classList.remove('is-dragging');
        handle.removeAttribute('aria-grabbed');
      };
      document.addEventListener('pointerup', finish);
      document.addEventListener('pointercancel', cancel);
    }

    moveRankTo(id, targetId, placeAfter) {
      const order = this.state.ranking.filter(itemId => itemId !== id);
      let targetIndex = order.indexOf(targetId);
      if (targetIndex < 0) return;
      if (placeAfter) targetIndex += 1;
      order.splice(targetIndex, 0, id);
      this.state.ranking = order;
      this.state.winnerId = '';
      this.state.outcome = '';
      this.save();
      this.renderResults();
      const announcer = this.root.querySelector('[data-announcer]');
      const item = this.byId(id);
      if (announcer && item) announcer.textContent = `${item.name} moved to position ${this.state.ranking.indexOf(id) + 1} of ${this.state.ranking.length}`;
    }
    moveRank(id, direction) {
      const from = this.state.ranking.indexOf(id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= this.state.ranking.length) return;
      [this.state.ranking[from], this.state.ranking[to]] = [this.state.ranking[to], this.state.ranking[from]];
      this.state.winnerId = '';
      this.state.outcome = '';
      this.save();
      this.renderResults();
    }

    renderDecision() {
      this.prepareRanking();
      const host = this.currentStep().querySelector('[data-decision]');
      if (!host) return;
      host.innerHTML = this.state.ranking.slice(0, 2).map(id => {
        const item = this.byId(id);
        return `<button class="resonance__choice" type="button" data-winner="${id}" aria-pressed="${this.state.winnerId === id}">${escapeHtml(item.name)}</button>`;
      }).join('');
      const next = this.currentStep().querySelector('[data-next]');
      if (next) next.disabled = !this.state.winnerId;
    }

    chooseWinner(id) {
      this.state.winnerId = id;
      this.state.outcome = '';
      this.state.ranking = [id, ...this.state.ranking.filter(itemId => itemId !== id)];
      this.save();
      this.renderDecision();
    }

    renderOutcome() {
      const config = this.config.outcome || {};
      const winner = this.byId(this.state.winnerId || this.state.ranking[0]);
      const fragrance = winner?.name || 'Your fragrance';
      const personalize = value => String(value || '').split('[fragrance]').join(fragrance);
      const lede = this.currentStep().querySelector('[data-outcome-lede]');
      const confirmHeading = this.currentStep().querySelector('[data-confirm-heading]');
      const confirmText = this.currentStep().querySelector('[data-confirm-text]');
      if (winner && lede) lede.textContent = personalize(config.winnerText);
      if (confirmHeading) confirmHeading.textContent = personalize(config.confirmHeading);
      if (confirmText) confirmText.textContent = personalize(config.confirmText);
      this.currentStep().querySelectorAll('[data-outcome]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.outcome === this.state.outcome)));
      const next = this.currentStep().querySelector('[data-next]');
      if (next) next.disabled = !this.state.outcome;
    }

    chooseOutcome(outcome) {
      this.state.outcome = outcome;
      this.save();
      this.renderOutcome();
    }

    renderContact() {
      const config = this.config.contact || {};
      const confirm = this.state.outcome === 'confirm';
      const winner = this.byId(this.state.winnerId || this.state.ranking[0]);
      const fragrance = winner?.name || 'your chosen fragrance';
      const heading = this.currentStep().querySelector('[data-contact-heading]');
      const text = this.currentStep().querySelector('[data-contact-text]');
      const notesLabel = this.currentStep().querySelector('[data-contact-notes-label]');
      const notes = this.currentStep().querySelector('[name="notes"]');
      const submit = this.currentStep().querySelector('[data-contact-submit]');
      if (heading) heading.textContent = confirm ? config.confirmHeading : config.reviewHeading;
      if (text) {
        const copy = confirm ? config.confirmText : config.reviewText;
        text.textContent = String(copy || '').split('[fragrance]').join(fragrance);
      }
      if (notesLabel) notesLabel.textContent = confirm ? config.confirmNotesLabel : config.reviewNotesLabel;
      if (notes) notes.placeholder = confirm ? config.confirmNotesPlaceholder : config.reviewNotesPlaceholder;
      if (submit) {
        submit.textContent = confirm ? config.confirmButton : config.reviewButton;
        submit.classList.toggle('resonance__button--gold', confirm);
      }
    }
    renderSuccess() {
      const config = this.config.success || {};
      const confirm = this.state.outcome === 'confirm';
      const winner = this.byId(this.state.winnerId || this.state.ranking[0]);
      const fragrance = winner?.name || 'your selected fragrance';
      const personalize = value => String(value || '').split('[fragrance]').join(fragrance);
      const heading = this.currentStep().querySelector('[data-success-heading]');
      const text = this.currentStep().querySelector('[data-success-text]');
      const timeline = this.currentStep().querySelector('[data-success-timeline]');
      const steps = confirm ? config.confirmSteps : config.reviewSteps;
      if (heading) heading.textContent = personalize(confirm ? config.confirmHeading : config.reviewHeading);
      if (text) text.textContent = personalize(confirm ? config.confirmText : config.reviewText);
      if (timeline) timeline.innerHTML = (steps || []).map(step => `<li><span class="resonance__timeline-dot" aria-hidden="true"></span><span class="resonance__timeline-copy"><strong>${escapeHtml(personalize(step?.[0]))}</strong><span>${escapeHtml(personalize(step?.[1]))}</span></span></li>`).join('');
    }
    payload(formData) {
      const winner = this.byId(this.state.winnerId || this.state.ranking[0]);
      return {
        platform: 'Resonance',
        submitted_at: new Date().toISOString(),
        shop_domain: window.Shopify?.shop || '',
        section_id: this.root.dataset.sectionId,
        outcome: this.state.outcome === 'confirm' ? 'fragrance_confirmed' : 'submit_for_review',
        confirmed_fragrance: this.state.outcome === 'confirm' ? winner?.name || '' : '',
        strongest_match: winner?.name || '',
        contact: { name: formData.get('name'), business: formData.get('business'), email: formData.get('email'), account_manager: formData.get('account_manager') },
        final_thoughts: formData.get('notes'),
        ranking: this.state.ranking.map(id => this.byId(id)?.name).filter(Boolean),
        evaluations: this.state.fragrances.map((item, index) => ({ order: index + 1, id: item.id, name: item.name, first_impression: item.first, dry_down: item.settle, character: item.sliders, affinity_score: item.affinity, affinity: this.affinityWord(item.affinity), notes: item.notes }))
      };
    }

    async submit(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const message = form.querySelector('[data-form-message]');
      if (!form.reportValidity()) return;
      const formData = new FormData(form);
      if (formData.get('website')) return;
      const endpoint = this.root.dataset.endpoint;
      const button = form.querySelector('[type=submit]');
      if (!endpoint) {
        message.textContent = 'Add the server endpoint in this section’s theme settings.';
        return;
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      message.textContent = '';
      try {
        const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(this.payload(formData)) });
        if (!response.ok) throw new Error(`Submission failed: ${response.status}`);
        if (this.root.dataset.saveProgress === 'true') sessionStorage.removeItem(this.key);
        const success = this.typeIndex('success');
        this.go(success >= 0 ? success : this.index + 1);
      } catch (error) {
        console.error('[Resonance]', error);
        message.textContent = this.config.labels.submissionError;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  const instances = new WeakMap();
  const init = scope => scope.querySelectorAll(SELECTOR).forEach(root => {
    if (!instances.has(root)) instances.set(root, new ResonanceEvaluation(root));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:block:select', event => {
    const root = event.target.closest(SELECTOR);
    const step = event.target.closest('[data-step]');
    const instance = root && instances.get(root);
    if (instance && step) instance.go(Number(step.dataset.stepIndex));
  });
})();
